import { immutable as arrayUnique } from 'array-unique';

import * as api from '@gitbook/api-client';
import { validateContentReference } from '@gitbook/app-content-refs';
import type { Change } from '@gitbook/slate';

import type {
    BlockTableData,
    TableCardsView,
    TableDefinitionImageRecord,
    TableDefinitions,
    TableRecord,
    TableRecords,
} from '../../types';
import { insertColumn, setColumnDefinition, setRecordValue, setView } from '../changes';
import { createColumnFromType } from '../utils';
import type { ViewValidationSpec } from './types';

/** Validation for a cards view */
export const cardsViewSpec: ViewValidationSpec<TableCardsView> = {
    normalize: (view, table) => {
        let result = view;
        const colDefs = Object.keys(table.definition);

        if (typeof result.hideColumnTitle !== 'boolean') {
            result = {
                ...result,
                hideColumnTitle: false,
            };
        }

        if (!result.cardSize || !['medium', 'large'].includes(result.cardSize)) {
            result = {
                ...result,
                cardSize: 'medium',
            };
        }

        // At least one column is required if there is no cover definition
        if (!Array.isArray(result.columns) || result.columns.length === 0) {
            // If there is no cover value, we keep the non-cover columns
            if (!!result.coverDefinition && !hasCoverValue(table.records, result.coverDefinition)) {
                result = {
                    ...result,
                    columns: colDefs.filter(
                        (key) => ![result.coverDefinition, result.coverDefinitionDark].includes(key)
                    ),
                };
            }
        } else {
            // Column orders should have unique columns
            const unique = arrayUnique(result.columns).filter((key) => colDefs.includes(key));
            if (unique.length !== result.columns.length) {
                result = {
                    ...result,
                    columns: unique,
                };
            }
        }

        if (
            result.coverDefinition &&
            !validateCoverDefinition(result.coverDefinition, table.definition)
        ) {
            result = {
                ...result,
                coverDefinition: undefined,
                coverDefinitionDark: undefined,
            };
        }

        // If the coverDefinition is not set, we remove the coverDefinitionDark
        if (
            result.coverDefinitionDark &&
            (!result.coverDefinition ||
                !validateCoverDefinition(result.coverDefinitionDark, table.definition))
        ) {
            result = {
                ...result,
                coverDefinitionDark: undefined,
            };
        }

        // The targetDefinition should be a valid content-ref definition
        if (
            typeof result.targetDefinition !== 'undefined' &&
            (typeof result.targetDefinition !== 'string' ||
                table.definition[result.targetDefinition]?.type !== 'content-ref')
        ) {
            result = {
                ...result,
                targetDefinition: undefined,
            };
        }

        return result as TableCardsView;
    },
};

function hasCoverValue(records: TableRecords, coverDefinition: string) {
    return Object.values(records).some((record) => {
        const coverValue = record.values[coverDefinition] as TableDefinitionImageRecord;
        if (!coverValue) {
            return false;
        }

        // Handle new format with ref and objectFit
        if (isExpandedImageRecordFormat(coverValue)) {
            return validateRef(coverValue.ref);
        }

        // Handle legacy format (direct ContentRef)
        return validateRef(coverValue);
    });
}

/**
 * Validate the cover definition. It should be a string and a column definition that is either a files or image column.
 */
function validateCoverDefinition(coverDefinition: string, definitions: TableDefinitions): boolean {
    if (typeof coverDefinition !== 'string' || !definitions[coverDefinition]) {
        return false;
    }

    if (!['files', 'image'].includes(definitions[coverDefinition].type)) {
        return false;
    }

    return true;
}

/**
 * Update the cover image for a record. It creates if a new column definition if none is specified in the view.
 */
export function setCardCoverForRecord(
    change: Change,
    tableKey: string,
    recordId: string,
    coverRef: api.ContentRefFile | api.ContentRefURL,

    /**
     * light: only set the light cover image.
     * dark: only set the dark cover image. Should throw an exception if a light cover image is not set.
     * both: set both light and dark cover images. This is equivalent to setting the light and removing the dark ref.
     */
    mode?: 'light' | 'dark' | 'both'
) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    // Prevent setting dark cover without a light cover definition
    if (mode === 'dark' && !tableData.view.coverDefinition) {
        throw new Error('Cannot set dark cover without a light cover definition');
    }

    // Get the applied cover definition for the current theme
    let appliedCoverDefinition = (() => {
        if (mode === 'dark') {
            return tableData.view.coverDefinitionDark
                ? tableData.definition[tableData.view.coverDefinitionDark]
                : undefined;
        }

        return tableData.view.coverDefinition
            ? tableData.definition[tableData.view.coverDefinition]
            : undefined;
    })();

    // If the column doesn't exist already, create it.
    if (!appliedCoverDefinition || !['files', 'image'].includes(appliedCoverDefinition.type)) {
        const title = mode === 'dark' ? 'Cover image (dark)' : 'Cover image';
        appliedCoverDefinition = createColumnFromType('image', title);
        insertColumn(change, tableKey, appliedCoverDefinition);
        setView(change, tableKey, {
            ...tableData.view,
            // Set the cover definition for the current theme
            // Make sure to use coverDefinitionDark only if coverDefinition is set
            [mode === 'dark' ? 'coverDefinitionDark' : 'coverDefinition']:
                appliedCoverDefinition.id,
        });
    }

    // If we are setting both, we remove the coverDefinitionDark
    if (mode === 'both' && tableData.view.coverDefinitionDark) {
        setView(change, tableKey, {
            ...tableData.view,
            coverDefinitionDark: undefined,
        });
    }

    const coverDefinitionColumnId = tableData.view.coverDefinition;
    const coverDefinitionColumn = coverDefinitionColumnId
        ? tableData.definition[coverDefinitionColumnId]
        : undefined;

    // Cover definitions used to use the "files" column type. Now they use the "image" type. Convert the old "files" to "image".
    if (
        coverDefinitionColumnId &&
        coverDefinitionColumn &&
        coverDefinitionColumn.type === 'files'
    ) {
        setColumnDefinition(
            change,
            tableKey,
            coverDefinitionColumnId,
            createColumnFromType('image', coverDefinitionColumn.title || 'Cover image'),
            { normalize: false }
        );

        Object.keys(tableData.records).forEach((recordId) => {
            const previousValue = tableData.records[recordId].values[coverDefinitionColumnId];

            const newValue = (() => {
                if (!previousValue) {
                    return null;
                }

                return {
                    kind: 'file',
                    // file column type is either an array of files or a single file
                    file: Array.isArray(previousValue) ? previousValue[0] : previousValue,
                };
            })();

            setRecordValue(change, tableKey, recordId, coverDefinitionColumnId, newValue, {
                normalize: false,
            });
        });

        // Save all normalization until the end of the migration
        change.normalize();
    }

    setRecordValue(change, tableKey, recordId, appliedCoverDefinition.id, coverRef);
}

const validateRef = validateContentReference(['file', 'url']);

/**
 * Get the cover for a record.
 */
export function getRecordCover(props: {
    coverDefinition?: string;
    coverDefinitionDark?: string;
    definition: TableDefinitions;
    record: TableRecord;
    currentTheme: 'light' | 'dark';
}) {
    const { coverDefinition, coverDefinitionDark, definition, record, currentTheme } = props;

    if (!coverDefinition && !coverDefinitionDark) {
        return null;
    }

    // Get the cover definition for the current theme
    const themedCoverDefinition =
        coverDefinitionDark && currentTheme === 'dark' ? coverDefinitionDark : coverDefinition;

    if (!themedCoverDefinition) {
        return null;
    }

    const coverColumnDef = definition[themedCoverDefinition];
    const coverValue = record.values[themedCoverDefinition];

    // If we are on dark mode and there is no cover value, we return the light cover value
    if (currentTheme === 'dark' && !coverValue) {
        return getRecordCover({
            ...props,
            currentTheme: 'light',
        });
    }

    if (!coverColumnDef || coverValue == null) {
        return null;
    }

    // Handle legacy files column (string)
    if (typeof coverValue === 'string') {
        return { kind: 'file' as const, file: coverValue };
    }

    // Handle array values
    if (Array.isArray(coverValue) && coverValue.length > 0) {
        const firstValue = coverValue[0];

        // Files column: array of strings (file IDs)
        if (typeof firstValue === 'string') {
            return { kind: 'file' as const, file: firstValue };
        }
    }

    // Handle new format with ref and objectFit FIRST
    if (coverValue && typeof coverValue === 'object' && 'ref' in coverValue) {
        const typedValue = coverValue as { ref: unknown };
        if (validateRef(typedValue.ref)) {
            return typedValue.ref;
        }
    }

    // Handle single content reference (legacy format)
    if (validateRef(coverValue)) {
        return coverValue;
    }

    return null;
}

/**
 * Remove the cover image for a record.
 */
export function removeCardCoverForRecord(change: Change, tableKey: string, recordId: string) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    if (!tableData.view.coverDefinition && !tableData.view.coverDefinitionDark) {
        return;
    }

    if (tableData.view.coverDefinition) {
        setRecordValue(change, tableKey, recordId, tableData.view.coverDefinition, null);
    }

    if (tableData.view.coverDefinitionDark) {
        setRecordValue(change, tableKey, recordId, tableData.view.coverDefinitionDark, null);
    }
}

/**
 * Helper function to check if a value is in the new format with ref and objectFit
 */
export function isExpandedImageRecordFormat(value: TableDefinitionImageRecord): value is {
    ref: api.ContentRefFile | api.ContentRefURL;
    objectFit?: api.CardsImageObjectFit;
} {
    return value !== null && typeof value === 'object' && 'ref' in value;
}

/**
 * Helper function to get the actual ContentRef from either format
 */
export function getContentRefFromImageValue(
    value: TableDefinitionImageRecord
): api.ContentRefFile | api.ContentRefURL {
    return isExpandedImageRecordFormat(value)
        ? value.ref
        : (value as api.ContentRefFile | api.ContentRefURL);
}

/**
 * Helper function to get objectFit from either format
 */
function getObjectFitFromImageValue(value: TableDefinitionImageRecord): api.CardsImageObjectFit {
    if (isExpandedImageRecordFormat(value) && value.objectFit) {
        return value.objectFit;
    }
    return api.CardsImageObjectFit.Cover;
}

export function setCardCoverObjectFitForRecord(
    change: Change,
    tableKey: string,
    recordId: string,
    objectFit: api.CardsImageObjectFit,
    currentTheme: 'light' | 'dark' = 'light'
) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover object fit for a non-cards view');
    }

    if (!Object.values(api.CardsImageObjectFit).includes(objectFit)) {
        throw new Error('Invalid cover object fit. Must be contain, fill, or cover.');
    }

    // Get the cover definition for the current theme
    const themedCoverDefinition =
        tableData.view.coverDefinitionDark && currentTheme === 'dark'
            ? tableData.view.coverDefinitionDark
            : tableData.view.coverDefinition;

    if (!themedCoverDefinition) {
        return;
    }

    const currentValue = tableData.records[recordId]?.values[
        themedCoverDefinition
    ] as TableDefinitionImageRecord;
    if (!currentValue) {
        return;
    }

    // Check if the objectFit is already set to avoid infinite loops
    if (getObjectFitFromImageValue(currentValue) === objectFit) {
        return;
    }

    // Create the new value in the new format
    const contentRef = getContentRefFromImageValue(currentValue);
    const updatedValue: TableDefinitionImageRecord = {
        ref: contentRef,
        objectFit,
    };

    setRecordValue(change, tableKey, recordId, themedCoverDefinition, updatedValue);
}

export function getRecordCoverObjectFit(props: {
    coverDefinition?: string;
    coverDefinitionDark?: string;
    definition: TableDefinitions;
    record: TableRecord;
    currentTheme: 'light' | 'dark';
}): api.CardsImageObjectFit {
    const { coverDefinition, coverDefinitionDark, record, currentTheme } = props;

    if (!coverDefinition && !coverDefinitionDark) {
        return api.CardsImageObjectFit.Cover;
    }

    // Get the cover definition for the current theme
    const themedCoverDefinition =
        coverDefinitionDark && currentTheme === 'dark' ? coverDefinitionDark : coverDefinition;

    if (!themedCoverDefinition) {
        return api.CardsImageObjectFit.Cover;
    }

    const coverValue = record.values[themedCoverDefinition] as TableDefinitionImageRecord;

    // If we are on dark mode and there is no cover value, try the light cover value
    if (currentTheme === 'dark' && !coverValue && coverDefinition) {
        const lightCoverValue = record.values[coverDefinition] as TableDefinitionImageRecord;
        if (lightCoverValue) {
            return getObjectFitFromImageValue(lightCoverValue);
        }
    }

    // Get objectFit from the cover value using helper function
    if (coverValue) {
        return getObjectFitFromImageValue(coverValue);
    }

    return api.CardsImageObjectFit.Cover;
}

/**
 * Update the cover image for a record. It creates if a new column definition if none is specified in the view.
 */
export function setCardTargetForRecord(
    change: Change,
    tableKey: string,
    recordId: string,
    targetRef: api.ContentRef
) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    let appliedTargetDefinition = tableData.view.targetDefinition
        ? tableData.definition[tableData.view.targetDefinition]
        : undefined;

    if (!appliedTargetDefinition || appliedTargetDefinition.type !== 'content-ref') {
        appliedTargetDefinition = createColumnFromType('content-ref');
        insertColumn(change, tableKey, appliedTargetDefinition);
        setView(change, tableKey, {
            ...tableData.view,
            targetDefinition: appliedTargetDefinition.id,
        });
    }

    setRecordValue(change, tableKey, recordId, appliedTargetDefinition.id, targetRef);
}

/**
 * Remove the target for the card associated with a record.
 */
export function removeCardTargetForRecord(change: Change, tableKey: string, recordId: string) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    if (!tableData.view.targetDefinition) {
        return;
    }

    setRecordValue(change, tableKey, recordId, tableData.view.targetDefinition, null);
}
